package org.zest.insan_360_website.utils

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.css.*
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.framework.annotations.DelicateApi
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.breakpoint.rememberBreakpoint
import kotlinx.coroutines.delay
import org.jetbrains.compose.web.css.FlexDirection
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.percent
import org.zest.insan_360_website.components.layouts.PageContentContainer
import org.zest.insan_360_website.components.layouts.containerShadowStyle
import org.zest.insan_360_website.components.layouts.userPageButtonColor
import org.zest.insan_360_website.model.CreditSessionPayload
import kotlin.js.Date
import kotlin.time.Duration.Companion.milliseconds

data class TimerState(
    val isExpired: Boolean,
    val formattedTime: String,
    val remainingMillis: Double,
    val activePercentage: Double,
    val extensionCount: Int
)

object SessionTimerUtils {

    fun isSessionExpired(sessionPayload: CreditSessionPayload?): Boolean {
        if (sessionPayload == null || !sessionPayload.hasActiveSession || sessionPayload.sessionExpiresAt == null || sessionPayload.serverTimeAtResponse == null) return true
        val targetTime = sessionPayload.sessionExpiresAt.toEpochMilliseconds().toDouble()
        val serverTime = sessionPayload.serverTimeAtResponse.toEpochMilliseconds().toDouble()
        return (targetTime - serverTime) <= 0.0
    }

    @Composable
    fun rememberSessionTimerState(
        sessionPayload: CreditSessionPayload?,
        baseSessionMillis: Double = 10 * 60 * 1000.0,
        extensionMillis: Double = 2 * 60 * 1000.0,
        onExpire: suspend () -> Unit = {}
    ): TimerState {
        if (sessionPayload == null || !sessionPayload.hasActiveSession || sessionPayload.sessionExpiresAt == null) {
            return TimerState(
                isExpired = true,
                formattedTime = "--:--",
                remainingMillis = 0.0,
                activePercentage = 0.0,
                extensionCount = 0
            )
        }

        var remainingMillis by remember { mutableStateOf(0.0) }
        var activePercentage by remember { mutableStateOf(100.0) }
        var isExpired by remember { mutableStateOf(false) }
        var formattedTime by remember { mutableStateOf("--:--") }

        LaunchedEffect(sessionPayload.sessionExpiresAt, sessionPayload.extensionCount) {
            if (sessionPayload.serverTimeAtResponse == null) return@LaunchedEffect
            val targetTime = sessionPayload.sessionExpiresAt.toEpochMilliseconds().toDouble()
            val serverTime = sessionPayload.serverTimeAtResponse.toEpochMilliseconds().toDouble()
            val localTimeAtFetch = Date().getTime()
            val offset = serverTime - localTimeAtFetch

            val currentExtensionCount = sessionPayload.extensionCount

            val activeSegmentMaxMillis = if (currentExtensionCount > 0) {
                extensionMillis
            } else {
                baseSessionMillis
            }

            while (true) {
                val currentTime = Date().getTime() + offset
                val diff = targetTime - currentTime

                if (diff <= 0.0) {
                    isExpired = true
                    remainingMillis = 0.0
                    activePercentage = 0.0
                    formattedTime = "00:00"
                    onExpire()
                    break
                }

                isExpired = false
                remainingMillis = diff

                activePercentage = ((diff / activeSegmentMaxMillis) * 100).coerceIn(0.0, 100.0)

                val minutes = ((diff % (1000 * 60 * 60)) / (1000 * 60)).toInt()
                val seconds = ((diff % (1000 * 60)) / 1000).toInt()

                val mStr = minutes.toString().padStart(2, '0')
                val sStr = seconds.toString().padStart(2, '0')
                formattedTime = "$mStr:$sStr"

                delay(1000.milliseconds)
            }
        }

        return TimerState(
            isExpired = isExpired,
            formattedTime = formattedTime,
            remainingMillis = remainingMillis,
            activePercentage = activePercentage,
            extensionCount = sessionPayload.extensionCount
        )
    }
}

@Composable
fun SessionTimerText(
    timerState: TimerState,
    modifier: Modifier = Modifier
) {
    SpanText(
        text = timerState.formattedTime,
        modifier = modifier
    )
}

@OptIn(DelicateApi::class)
@Composable
fun SessionTimerBar(
    timerState: TimerState,
    modifier: Modifier = Modifier
) {
    val breakpoint = rememberBreakpoint()
    val isTablet = breakpoint >= Breakpoint.ZERO && breakpoint <= Breakpoint.MD

    if (timerState.isExpired && timerState.remainingMillis <= 0.0) return

    val isBaseTimer = timerState.extensionCount == 0
    val borderRadius = 0.5.cssRem

    PageContentContainer(
        modifier
            .fillMaxWidth(40.percent)
            .height(Height.FitContent)
            .borderRadius(borderRadius)
            .overflow(Overflow.Clip)
            .gap(0.5.cssRem)
            .flexDirection(FlexDirection.Row)
            .padding(0.5.cssRem)
            .borderRadius(1.cssRem)
            .userSelect(UserSelect.None)
            .then(containerShadowStyle.toModifier()),
    ) {
        if (!isTablet || isBaseTimer) {
            Box(
                Modifier
                    .flex(10)
                    .fillMaxWidth()
                    .height(1.cssRem)
                    .borderRadius(borderRadius)
                    .overflow(Overflow.Clip)
                    .backgroundColor(Colors.LightGray.darkened(0.1f)),
                contentAlignment = Alignment.CenterEnd
            ) {
                val progression =
                    if (isBaseTimer) timerState.activePercentage
                    else 0
                Box(
                    Modifier
                        .fillMaxWidth(progression.percent)
                        .fillMaxHeight()
                        .backgroundColor(userPageButtonColor)
                        .borderRadius(borderRadius)
                )
                if (isBaseTimer) {
                    SessionTimerText(
                        timerState = timerState,
                        modifier = Modifier
                            .color(Colors.White)
                            .fontWeight(FontWeight.Bold)
                            .fontSize(0.75.cssRem)
                            .justifySelf(JustifySelf.Center)
                    )
                } else {
                    SpanText(
                        "-",
                        Modifier
                            .color(Colors.White)
                            .fontWeight(FontWeight.Bold)
                            .fontSize(0.75.cssRem)
                            .justifySelf(JustifySelf.Center)
                    )
                }
            }
        }
        if (!isBaseTimer) {
            repeat(timerState.extensionCount) { index ->
                val active = index == timerState.extensionCount - 1
                val progression = if (active) timerState.activePercentage else 0
                if (!isTablet || active) {
                    Box(
                        Modifier
                            .flex(2)
                            .fillMaxWidth()
                            .height(1.cssRem)
                            .borderRadius(borderRadius)
                            .overflow(Overflow.Clip)
                            .backgroundColor(Colors.LightGray.darkened(0.1f)),
                        contentAlignment = Alignment.CenterEnd
                    ) {
                        Box(
                            Modifier
                                .fillMaxWidth(progression.percent)
                                .fillMaxHeight()
                                .backgroundColor(Colors.Red)
                                .borderRadius(borderRadius)
                        )
                        if (active) {
                            SessionTimerText(
                                timerState = timerState,
                                modifier = Modifier
                                    .color(Colors.White)
                                    .fontWeight(FontWeight.Bold)
                                    .fontSize(0.75.cssRem)
                                    .justifySelf(JustifySelf.Center)
                            )
                        } else {
                            SpanText(
                                "-",
                                Modifier
                                    .color(Colors.White)
                                    .fontWeight(FontWeight.Bold)
                                    .fontSize(0.75.cssRem)
                                    .justifySelf(JustifySelf.Center)
                            )
                        }
                    }
                }
            }
        }
    }
}